/**
 * 404 page — catch-all for unknown SPA routes.
 *
 * Mounted in `App.tsx` as the last `<Route path="*">` so any URL that
 * doesn't match a real route renders this instead of a blank white
 * canvas. Mirrors the styling of the rest of the console.
 */
import type { JSX } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Compass, Home, ArrowLeft } from 'lucide-react';

import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';

export default function NotFound(): JSX.Element {
  const location = useLocation();
  return (
    <>
      <PageHeader
        eyebrow="404"
        title="Page not found"
        description="That URL isn't a route in the gateway portal."
      >
        <Link to="/">
          <ActionButton icon={Home} primary>
            Command Center
          </ActionButton>
        </Link>
        <button
          type="button"
          onClick={() => window.history.back()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            border: '1px solid var(--line-strong)',
            background: 'transparent',
            borderRadius: 8,
            fontSize: 13,
            cursor: 'pointer',
            marginLeft: 8,
          }}
        >
          <ArrowLeft size={14} aria-hidden /> Back
        </button>
      </PageHeader>

      <SectionCard
        icon={Compass}
        title="What happened"
        text={`The path "${location.pathname}" doesn't match any known route. It may have been moved, hidden from the sidebar, or never existed.`}
      >
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>
          <li>
            Looking for tools? Try <Link to="/access">My Access</Link>.
          </li>
          <li>
            Managing servers? Open <Link to="/build/publisher">Publisher Workspace</Link>.
          </li>
          <li>
            Checking who can call what? <Link to="/admin/access">Access Matrix</Link>.
          </li>
          <li>
            Operations dashboard? <Link to="/operate/runtime">Runtime Health</Link>.
          </li>
        </ul>
      </SectionCard>
    </>
  );
}
